import { datadogRum } from '@datadog/browser-rum'
import { isBotUserAgent } from 'lib/helpers/isBotUserAgent/isBotUserAgent'
import { getBackendEnvironment } from 'lib/helpers/getBackendEnvironment/getBackendEnvironment'
import { hasUrlSecrets } from 'lib/helpers/redactUrlSecrets/redactUrlSecrets'
import { isAbortedRouteChangeError } from 'lib/tracking/helpers/isAbortedRouteChangeError/isAbortedRouteChangeError'
import { isHardNavigateSameUrlError } from 'lib/tracking/helpers/isHardNavigateSameUrlError/isHardNavigateSameUrlError'
import { isAutoCapturedChunkLoadError } from 'lib/tracking/helpers/isAutoCapturedChunkLoadError/isAutoCapturedChunkLoadError'
import { isLaunchDarklyInitTimeoutError } from 'lib/tracking/helpers/isLaunchDarklyInitTimeoutError/isLaunchDarklyInitTimeoutError'
import { isCancelledOperationError } from 'lib/tracking/helpers/isCancelledOperationError/isCancelledOperationError'
import { isThirdPartyThrownError } from 'lib/tracking/helpers/isThirdPartyThrownError/isThirdPartyThrownError'
import { getGraphQLErrors } from 'lib/helpers/getGraphQLErrors/getGraphQLErrors'
import { isBenignNetworkError } from 'lib/helpers/isBenignNetworkError/isBenignNetworkError'
import { isNetworkError } from 'lib/helpers/isNetworkError/isNetworkError'
import { isUnauthenticatedError } from 'lib/helpers/isUnauthenticatedError/isUnauthenticatedError'
import { isReactBoundarySilencedError } from 'lib/tracking/helpers/isReactBoundarySilencedError/isReactBoundarySilencedError'
import { redactRumEvent } from 'lib/tracking/helpers/redactRumEvent/redactRumEvent'
import type { UserTraits } from './types'

type ErrorParams = Record<string, unknown>

interface ErrorNoiseFilter {
  predicate: (params: { message: string; source: string; stack?: string; error?: unknown }) => boolean
  onlyWhenBackgrounded: boolean
}

/**
 * Predicates matching classes of non-actionable RUM error noise.
 *
 * Only errors we generate belong here. A vendor's error under a stable message is ignored in Datadog
 * Error Tracking instead (reversible, keeps the evidence), unless the decision needs per-event
 * evidence Datadog cannot condition on: `view.in_foreground`, the thrown value, or the top stack frame.
 *
 * `onlyWhenBackgrounded` gates a filter to backgrounded tabs (`view.in_foreground === false`): a
 * fetch-abort message is benign when the user has navigated away, but the same message on a live
 * foreground tab can be a real outage or broken CORS and must still be reported.
 *
 * Predicates receive the reporting `source` alongside the message so a filter can spare the
 * app's own `logError` reports (`source: custom`) while dropping RUM's auto-captured duplicates,
 * and the original thrown value as `error`, whose raw stack keeps frames Datadog's serialization
 * drops.
 */
const errorNoiseFilters: ErrorNoiseFilter[] = [
  { predicate: isAbortedRouteChangeError, onlyWhenBackgrounded: false },
  { predicate: isHardNavigateSameUrlError, onlyWhenBackgrounded: false },
  { predicate: isAutoCapturedChunkLoadError, onlyWhenBackgrounded: false },
  { predicate: isCancelledOperationError, onlyWhenBackgrounded: false },
  { predicate: isThirdPartyThrownError, onlyWhenBackgrounded: false },
  { predicate: isBenignNetworkError, onlyWhenBackgrounded: true },
  { predicate: isLaunchDarklyInitTimeoutError, onlyWhenBackgrounded: true },
]

export const initDatadog = () => {
  const applicationId = process.env.DATADOG_APPLICATION_ID
  const clientToken = process.env.DATADOG_CLIENT_TOKEN
  // Local .env files might carry the production Datadog keys, so only VERCEL_ENV tells a local run apart.
  const isDeployed = process.env.VERCEL_ENV === 'production' || process.env.VERCEL_ENV === 'preview'

  // A crawler that executes JS (Baiduspider-render, OC-4533) is billed as a RUM session like any
  // user; robots.txt is advisory, so the gate has to live here.
  if (isDeployed && applicationId && clientToken && !isBotUserAgent()) {
    datadogRum.init({
      applicationId,
      clientToken,
      // Sandbox and demo also build as Vercel production; only their backend tells them apart.
      env:
        process.env.VERCEL_ENV === 'production'
          ? getBackendEnvironment({ backendUrl: process.env.BACKEND_APP_URL })
          : process.env.VERCEL_ENV,
      version: process.env.SHORT_SHA,
      site: 'datadoghq.com',
      service: 'settle-web',
      traceSampleRate: 100,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      profilingSampleRate: 10,
      trackLongTasks: true,
      trackResources: true,
      trackUserInteractions: true,
      allowUntrustedEvents: true,
      allowedTracingUrls: [process.env.BACKEND_APP_URL],
      defaultPrivacyLevel: 'mask-user-input',
      // Replay starts only on a clean URL, via `syncSessionReplayWithUrl`.
      startSessionReplayRecordingManually: true,
      beforeSend: (event, context) => {
        redactRumEvent({ event })

        if (event.type !== 'error') {
          return true
        }

        const originalError = 'error' in context ? context.error : undefined

        // Drop RUM's duplicate of an error a React boundary already handled without reporting.
        if (isReactBoundarySilencedError({ source: event.error.source, error: originalError })) {
          return false
        }

        // A request that never reached the server is not an application fault. Matching on Apollo's
        // link-chain registry rather than the message keeps this working across browsers, which word
        // the failure differently. The rate stays visible as the error link's
        // `graphql_transport_failure` action.
        if (isNetworkError(originalError)) {
          return false
        }

        // A 401 is owned by the Apollo error link's `/login` redirect, which does not unmount
        // React synchronously: every query still in flight at token expiry resolves with its 401
        // and reaches whichever path was waiting — a boundary, a mutation's `catch`, or nothing at
        // all — so the report shape varies while the outcome never does. The rate stays visible as
        // the error link's `graphql_unauthenticated` action.
        if (isUnauthenticatedError(originalError)) {
          return false
        }

        const { message, source, stack } = event.error
        const isBackgrounded = event.view?.in_foreground === false

        const isNoise = errorNoiseFilters.some(
          ({ predicate, onlyWhenBackgrounded }) =>
            (!onlyWhenBackgrounded || isBackgrounded) && predicate({ message, source, stack, error: originalError }),
        )

        return !isNoise
      },
    })

    syncSessionReplayWithUrl(window.location.href)
  }
}

/**
 * Replay records `location.href` in every snapshot with no way to redact it, and a start snapshots at
 * once: pass the URL the browser is on, never the one it is heading to.
 */
export const syncSessionReplayWithUrl = (url: string): void => {
  if (hasUrlSecrets({ url })) {
    datadogRum.stopSessionReplayRecording()
  } else {
    datadogRum.startSessionReplayRecording()
  }
}

export const stopSessionReplayOnSecretUrl = (url: string): void => {
  if (hasUrlSecrets({ url })) {
    datadogRum.stopSessionReplayRecording()
  }
}

export const addAction = (name: string, context?: Record<string, unknown>) => {
  datadogRum.addAction(name, context)
}

export const updateUser = (userId?: string | null, traits: UserTraits = {}) => {
  datadogRum.setUser({
    id: userId ?? undefined,
    userId,
    ...traits,
  })
}

/**
 * Can be used to log errors on client and server side
 */
export const logError = (err: unknown, params?: ErrorParams): void => {
  datadogRum.addError(err, params)
}

/**
 * Reports each GraphQL error the server returned as its own RUM error.
 *
 * Reports **nothing** for an error carrying no GraphQL errors — a network failure or a plain
 * `Error` is silently ignored. Use {@link logError} for those; this is not a general error sink.
 */
export const logGraphQLErrors = (err: unknown, params?: ErrorParams): void => {
  getGraphQLErrors(err).forEach((error) => logError(error, params))
}
